import { useState, useEffect } from 'react';
import { ValidationError } from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Modal,
    FormControl,
    FormLabel,
    Input,
    FormErrorMessage,
    useToast,
} from '@chakra-ui/react';

import ReactQueryKeys from '../../../constants/react-query-keys.types';
import { subscriptionSdk } from '../../../services/subscription.service';
import { Service, CreateService } from '../../../types/service.type';

import {
    createServiceFields,
    createServiceFormSchema,
} from './config/service-form.config';

interface Props {
    service: Service;
    isOpen: boolean;
    onClose: () => void;
}

const UpdateServiceModal = ({ service, isOpen, onClose }: Props) => {
    const [form, setForm] = useState<CreateService>({
        name: '',
        code: '',
        description: '',
        totalFreeTowingServices: 0,
        totalFreeAmbulanceServices: 0,
        towingEligibilityDays: 0,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const toast = useToast();

    const queryClient = useQueryClient();

    useEffect(() => {
        if (service) {
            setForm({
                name: service.name,
                code: service.code,
                description: service.description || '',
                totalFreeTowingServices: service.totalFreeTowingServices,
                totalFreeAmbulanceServices: service.totalFreeAmbulanceServices,
                towingEligibilityDays: service.towingEligibilityDays,
            });
        }
    }, [service]);

    const resetForm = () => {
        setForm({
            name: '',
            code: '',
            description: '',
            totalFreeTowingServices: 0,
            totalFreeAmbulanceServices: 0,
            towingEligibilityDays: 0,
        });
        setErrors({});
    };

    const addForm = (field: keyof CreateService, value: string | number) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const requestUpdate = async () => {
        try {
            await createServiceFormSchema.validate(form, { abortEarly: false });

            updateServiceMutation.mutate({ id: service.id, data: form });
        } catch (error) {
            if (error instanceof ValidationError) {
                const validationErrors: Record<string, string> = {};
                error.inner.forEach((error: any) => {
                    validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
            } else {
                console.error(error);
                alert('Error al actualizar el servicio');
            }
        }
    };

    const updateServiceMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: CreateService }) =>
            subscriptionSdk.updateService(id, data),
        onSuccess: (res) => {
            if (res.code !== 200) throw new Error(res.message);
            toast({
                description: 'Servicio actualizado con éxito',
                status: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: [ReactQueryKeys.SERVICES],
            });
            onClose();
        },
        onError: (error) =>
            toast({
                description: error.message,
                status: 'error',
            }),
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                resetForm();
                onClose();
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Actualizar servicio</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {createServiceFields.map((field) => (
                        <FormControl
                            key={field.key}
                            isInvalid={!!errors[field.key]}
                        >
                            <FormLabel>{field.label}</FormLabel>
                            <Input
                                type={field.type}
                                placeholder={field.placeholder}
                                value={form[field.key]}
                                onChange={(e) =>
                                    addForm(
                                        field.key,
                                        field.type === 'number'
                                            ? Number(e.target.value)
                                            : e.target.value
                                    )
                                }
                                required={field.required}
                            />

                            <FormErrorMessage>
                                {errors[field.key]}
                            </FormErrorMessage>
                        </FormControl>
                    ))}
                </ModalBody>

                <ModalFooter>
                    <Button
                        isLoading={updateServiceMutation.isPending}
                        colorScheme="primary"
                        onClick={requestUpdate}
                    >
                        Actualizar
                    </Button>
                    <Button
                        isLoading={updateServiceMutation.isPending}
                        variant="ghost"
                        mr={3}
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default UpdateServiceModal;
